/**
 * Het dossier van één held (SPEC §7.1): alles wat er over hem bekend is en
 * alle handelingen van het beheer.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type Helddossier, type OmgevingInfo } from '../../api';
import { Bevestigknop, Datum, Euro, Knop, Laden, Melding, Niveaubadge, Veld, useTitel } from '../../componenten/basis';
import { Logokiezer } from '../../componenten/Logokiezer';
import { MAXIMUM_INCASSO_CENT, alsEuro, uitInvoer } from '../../lib/geld';
import { NIVEAUNAMEN, afkoopbaar } from '../../lib/niveau';
import { magMuteren } from '../../lib/rollen';
import { BIJDRAGESTATUSNAMEN, SOORTNAMEN, WIJZENAMEN, type Logo } from '../../lib/vormen';
import { Status } from './Helden';
import { Logboektabel } from './Logboek';
import { Facturen } from './Facturen';

export function Dossier({ omgeving }: { omgeving: OmgevingInfo }) {
  const { id = '' } = useParams();
  const [d, zetD] = useState<Helddossier | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [melding, zetMelding] = useState<string | null>(null);
  useTitel(d ? d.naam : 'Dossier');
  const mag = magMuteren(omgeving.gebruiker?.rollen ?? []);

  const laad = useCallback(() => {
    api.haal<Helddossier>(`/api/beheer/helden/${id}`).then(zetD).catch((f: Error) => zetFout(f.message));
  }, [id]);

  useEffect(() => {
    laad();
  }, [laad]);

  async function doe(actie: string, inhoud: unknown = {}, tekst?: string) {
    zetFout(null);
    zetMelding(null);
    try {
      zetD(await api.stuur<Helddossier>(`/api/beheer/helden/${id}/${actie}`, inhoud));
      if (tekst) zetMelding(tekst);
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  async function bijdrage(bijdrageId: string, actie: 'betaald' | 'kwijtschelden', inhoud: unknown, tekst: string) {
    zetFout(null);
    zetMelding(null);
    try {
      zetD(await api.stuur<Helddossier>(`/api/beheer/bijdragen/${bijdrageId}/${actie}`, inhoud));
      zetMelding(tekst);
    } catch (f) {
      zetFout((f as Error).message);
    }
  }

  if (fout && !d) return <Melding soort="fout">{fout}</Melding>;
  if (!d) return <Laden />;

  const openRegels = d.bijdragen.filter((b) => b.status === 'open' || b.status === 'mislukt');

  return (
    <div className="space-y-6">
      <p className="text-sm">
        <Link to="/beheer/helden" className="text-inkt-zacht">
          ← Alle helden
        </Link>
      </p>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl">{d.soort === 'bedrijf' ? d.weergavenaam : d.naam}</h1>
          <p className="mt-1 text-sm text-inkt-zacht">
            {d.soort === 'bedrijf' && <>Bedrijf · contact {d.naam} · </>}
            {d.email}
            {d.telefoon && <> · {d.telefoon}</>} · <Status status={d.status} />
            {d.jubileum && <> · {d.jubileum} jaar held</>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Niveaubadge niveau={d.niveau} groot />
          <span className="text-sm text-inkt-zacht">
            {d.betaaldeJaren} {d.betaaldeJaren === 1 ? 'jaar' : 'jaren'}
            {d.afgekocht && <> · {NIVEAUNAMEN[d.afgekocht].toLowerCase()} afgekocht</>}
          </span>
        </div>
      </div>

      {melding && <Melding soort="goed">{melding}</Melding>}
      {fout && <Melding soort="fout">{fout}</Melding>}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="kaart p-5">
            <h2 className="text-xl">Bijdragen</h2>
            <table className="tabel mt-3">
              <thead>
                <tr>
                  <th>Wat</th>
                  <th>Jaar</th>
                  <th>Bedrag</th>
                  <th>Status</th>
                  {mag && <th />}
                </tr>
              </thead>
              <tbody>
                {d.bijdragen.map((b) => (
                  <tr key={b.id}>
                    <td>
                      {SOORTNAMEN[b.soort]}
                      {b.heldenjaar ? <span className="text-inkt-zacht"> · {b.heldenjaar}e jaar</span> : null}
                      {b.toelichting && <span className="block text-xs text-inkt-zacht">{b.toelichting}</span>}
                    </td>
                    <td className="text-inkt-zacht">{b.kalenderjaar ?? '-'}</td>
                    <td><Euro cent={b.bedrag_cent} /></td>
                    <td>
                      <span className={b.status === 'mislukt' ? 'text-rood' : b.status === 'betaald' ? 'text-groen' : ''}>{BIJDRAGESTATUSNAMEN[b.status]}</span>
                      {b.betaald_op && <span className="block text-xs text-inkt-zacht"><Datum waarde={b.betaald_op} />{b.wijze && <> · {WIJZENAMEN[b.wijze]}</>}</span>}
                      {b.status === 'open' && b.mollie_payment_id && <span className="block text-xs text-inkt-zacht">bij Mollie: {b.mollie_payment_id}</span>}
                      {b.status === 'open' && !b.mollie_payment_id && b.aangekondigd_op && <span className="block text-xs text-inkt-zacht">aangekondigd <Datum waarde={b.aangekondigd_op} /></span>}
                    </td>
                    {mag && (
                      <td className="text-right">
                        {(b.status === 'open' || b.status === 'mislukt') && (
                          <span className="flex flex-wrap justify-end gap-2">
                            <Bevestigknop soort="goud" vraag="Als betaald registreren?" onBevestig={() => bijdrage(b.id, 'betaald', { toelichting: 'Handmatig geregistreerd' }, 'Als betaald geregistreerd; de held heeft een mail gekregen.')}>
                              Betaald
                            </Bevestigknop>
                            <Bevestigknop soort="stil" vraag="Kwijtschelden? Telt als een betaald jaar." onBevestig={() => bijdrage(b.id, 'kwijtschelden', { reden: 'Kwijtgescholden door het bestuur' }, 'Kwijtgescholden.')}>
                              Kwijtschelden
                            </Bevestigknop>
                            {b.betaal_token && (
                              <a href={`/betalen/${b.betaal_token}`} className="knop knop-stil knop-klein" target="_blank" rel="noreferrer">
                                Betaallink
                              </a>
                            )}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <Facturen heldId={d.id} bedrijf={d.soort === 'bedrijf'} mag={mag} naVerandering={laad} />

          <Gegevens d={d} zetD={zetD} mag={mag} />

          <section className="kaart p-5">
            <h2 className="text-xl">Logboek van deze held</h2>
            <Logboektabel regels={d.logboek} zonderHeld />
          </section>
        </div>

        <aside className="space-y-6">
          <section className="kaart p-5 text-sm">
            <h2 className="text-xl">Incasso</h2>
            <dl className="mt-3 space-y-2">
              <div>
                <dt className="etiket">Mandaat</dt>
                <dd>{d.mandaat ? <>{d.mandaat.tenaamstelling ?? '-'} · ····{d.mandaat.laatste4 ?? '????'}</> : <span className="text-inkt-zacht">Geen - krijgt betaalverzoeken</span>}</dd>
              </div>
              {d.volgendeIncasso && (
                <div>
                  <dt className="etiket">Volgende</dt>
                  <dd><Euro cent={d.volgendeIncasso.bedrag_cent} /> rond <Datum waarde={d.volgendeIncasso.datum} /></dd>
                </div>
              )}
              <div>
                <dt className="etiket">Mollie</dt>
                <dd className="font-mono text-xs text-inkt-zacht">{d.mollie_customer_id ?? '-'}<br />{d.mollie_mandate_id ?? ''}</dd>
              </div>
              <div>
                <dt className="etiket">Held sinds</dt>
                <dd><Datum waarde={d.lid_sinds} /> <span className="text-inkt-zacht">· aangemeld <Datum waarde={d.aangemaakt_op} /></span></dd>
              </div>
              {d.opgezegd_op && (
                <div>
                  <dt className="etiket">Opgezegd</dt>
                  <dd><Datum waarde={d.opgezegd_op} />{d.opzegreden && <span className="block text-inkt-zacht">{d.opzegreden}</span>}</dd>
                </div>
              )}
            </dl>
          </section>

          {mag && (
            <section className="kaart p-5">
              <h2 className="text-xl">Handelingen</h2>
              <div className="mt-3 flex flex-col items-start gap-2">
                {(openRegels.some((b) => b.mollie_payment_id) || (d.mollie_customer_id && !d.mandaat)) && (
                  <Knop soort="stil" klein onClick={() => doe('synchroniseer', {}, 'Stand bij Mollie opgehaald.')}>
                    Stand bij Mollie ophalen
                  </Knop>
                )}
                {d.status !== 'opgezegd' && (
                  <Bevestigknop soort="stil" vraag="Betaalverzoek mailen?" onBevestig={() => doe('betaalverzoek', {}, 'Betaalverzoek verstuurd.')}>
                    Betaalverzoek sturen
                  </Bevestigknop>
                )}
                <Bevestigknop soort="stil" vraag="Inloglink mailen?" onBevestig={() => doe('inloglink', {}, 'Inloglink verstuurd.')}>
                  Inloglink sturen
                </Bevestigknop>
                {d.status === 'actief' &&
                  afkoopbaar(d.niveau).map((n) => (
                    <Bevestigknop key={n} soort="stil" vraag={`${NIVEAUNAMEN[n]} registreren als contant/bank betaald?`} onBevestig={() => doe('afkoop', { niveau: n }, `${NIVEAUNAMEN[n]} geregistreerd.`)}>
                      Afkoop {NIVEAUNAMEN[n].toLowerCase()} registreren
                    </Bevestigknop>
                  ))}
                {(d.status === 'opgezegd' || d.status === 'verlopen') && (
                  <Bevestigknop soort="goud" vraag="Weer actief zetten?" onBevestig={() => doe('heractiveren', {}, 'Weer actief.')}>
                    Heractiveren
                  </Bevestigknop>
                )}
                {d.status !== 'opgezegd' && (
                  <Bevestigknop vraag="Opzeggen namens deze held? Het mandaat wordt ingetrokken." onBevestig={() => doe('opzeggen', { reden: 'Opgezegd door het bestuur' }, 'Opgezegd; de held heeft een bevestiging gekregen.')}>
                    Opzeggen
                  </Bevestigknop>
                )}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function Gegevens({ d, zetD, mag }: { d: Helddossier; zetD: (d: Helddossier) => void; mag: boolean }) {
  const [invoer, zetInvoer] = useState({
    soort: d.soort,
    naam: d.naam,
    weergavenaam: d.weergavenaam,
    regel: d.regel ?? '',
    website: d.website ?? '',
    telefoon: d.telefoon ?? '',
    toon_op_muur: d.toon_op_muur,
    notitie_intern: d.notitie_intern ?? '',
    factuuradres: d.factuuradres ?? '',
  });
  const [bedragTekst, zetBedragTekst] = useState(d.jaarbijdrage_cent ? alsEuro(d.jaarbijdrage_cent).replace('€ ', '').replace(',-', '') : '');
  const [logo, zetLogo] = useState<Logo | null | undefined>(undefined);
  const [bezig, zetBezig] = useState(false);
  const [melding, zetMelding] = useState<string | null>(null);
  const eigen = bedragTekst.trim() ? uitInvoer(bedragTekst) : null;
  const bedragFout = bedragTekst.trim() && (eigen === null || eigen < d.tarief_cent) ? `Minimaal ${alsEuro(d.tarief_cent)}` : null;

  async function bewaar(e: FormEvent) {
    e.preventDefault();
    if (bedragFout) return;
    zetBezig(true);
    try {
      let nieuw = await api.stuur<Helddossier>(`/api/beheer/helden/${d.id}`, { ...invoer, jaarbijdrage_cent: eigen && eigen > d.tarief_cent ? eigen : null }, 'PUT');
      if (logo !== undefined) {
        nieuw = await api.stuur<Helddossier>(`/api/beheer/helden/${d.id}/logo`, { logo }, 'PUT');
        zetLogo(undefined);
      }
      zetD(nieuw);
      zetMelding('Opgeslagen.');
    } catch (f) {
      zetMelding((f as Error).message);
    } finally {
      zetBezig(false);
    }
  }

  const bedrijf = invoer.soort === 'bedrijf';

  return (
    <form onSubmit={bewaar} className="kaart space-y-4 p-5">
      <h2 className="text-xl">Gegevens</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Veld etiket="Soort">
          <select className="veld" disabled={!mag} value={invoer.soort} onChange={(e) => zetInvoer({ ...invoer, soort: e.target.value as 'persoon' | 'bedrijf' })}>
            <option value="persoon">Persoon</option>
            <option value="bedrijf">Bedrijf</option>
          </select>
        </Veld>
        <Veld etiket={bedrijf ? 'Contactpersoon' : 'Naam'}>
          <input className="veld" disabled={!mag} value={invoer.naam} onChange={(e) => zetInvoer({ ...invoer, naam: e.target.value })} />
        </Veld>
        <Veld etiket={bedrijf ? 'Bedrijfsnaam' : 'Naam op de muur'}>
          <input className="veld" disabled={!mag} maxLength={80} value={invoer.weergavenaam} onChange={(e) => zetInvoer({ ...invoer, weergavenaam: e.target.value })} />
        </Veld>
        <Veld etiket="Regel eronder">
          <input className="veld" disabled={!mag} maxLength={80} value={invoer.regel} onChange={(e) => zetInvoer({ ...invoer, regel: e.target.value })} />
        </Veld>
        <Veld etiket="Telefoon">
          <input className="veld" disabled={!mag} value={invoer.telefoon} onChange={(e) => zetInvoer({ ...invoer, telefoon: e.target.value })} />
        </Veld>
        <Veld etiket="Eigen jaarbedrag" hulp={`Leeg = tarief ${alsEuro(d.tarief_cent)}. Boven ${alsEuro(MAXIMUM_INCASSO_CENT)} geen incasso maar elk jaar een betaalverzoek.`} fout={bedragFout}>
          <div className="flex items-center gap-2">
            <span className="text-inkt-zacht">€</span>
            <input className="veld max-w-[10rem]" disabled={!mag} inputMode="decimal" value={bedragTekst} aria-invalid={Boolean(bedragFout)} onChange={(e) => zetBedragTekst(e.target.value)} />
          </div>
        </Veld>
        {bedrijf && (
          <>
            <Veld etiket="Website">
              <input className="veld" disabled={!mag} value={invoer.website} onChange={(e) => zetInvoer({ ...invoer, website: e.target.value })} />
            </Veld>
            <Logokiezer logo={logo ?? null} huidigeUrl={logo === undefined && d.heeft_logo ? `/api/beheer/helden/${d.id}/logo?t=${Date.now()}` : null} onChange={zetLogo} />
          </>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" className="accent-goud-500" disabled={!mag} checked={invoer.toon_op_muur} onChange={(e) => zetInvoer({ ...invoer, toon_op_muur: e.target.checked })} />
        Met naam op de muur
      </label>
      <Veld etiket="Factuuradres" hulp="Meerdere regels, voor op de factuur. Leeg = alleen de naam.">
        <textarea className="veld" rows={3} disabled={!mag} value={invoer.factuuradres} onChange={(e) => zetInvoer({ ...invoer, factuuradres: e.target.value })} />
      </Veld>
      <Veld etiket="Interne notitie" hulp="Ziet de held niet.">
        <textarea className="veld" rows={3} disabled={!mag} value={invoer.notitie_intern} onChange={(e) => zetInvoer({ ...invoer, notitie_intern: e.target.value })} />
      </Veld>
      {mag && (
        <div className="flex items-center gap-3">
          <Knop type="submit" soort="stil" klein bezig={bezig}>
            Opslaan
          </Knop>
          {melding && <span className="text-sm text-inkt-zacht">{melding}</span>}
        </div>
      )}
    </form>
  );
}
